<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import BrandHeader from "@/components/BrandHeader.vue";
import WhatsappForm from "@/components/WhatsappForm.vue";
import OtpForm from "@/components/OtpForm.vue";
import EventPreviewScreen from "@/components/EventPreviewScreen.vue";
import SubscriptionRequiredScreen from "@/components/SubscriptionRequiredScreen.vue";
import ErrorScreen from "@/components/ErrorScreen.vue";
import { useGateMachine } from "@/composables/useGateMachine";
import { useLocale } from "@/composables/useLocale";
import { ApiError, getEventDetail, getMe, joinEvent, sendOtp, validateOtp } from "@/services/api";
import { clearToken, hasValidToken, setToken } from "@/services/jwt";
import { resolveStoreUrl } from "@/services/mobileRedirect";
import { identifyUser, resetUser, track } from "@/services/telemetry";
import type { GateErrorCode, JoinErrorCode, JoinInlineError } from "@/types/gate";

const props = defineProps<{ eventId: string }>();

const machine = useGateMachine();
const { applyFromEnvironment } = useLocale();
const { t } = useI18n();

const otpFormRef = ref<InstanceType<typeof OtpForm> | null>(null);
const resendingOtp = ref(false);
// Last E.164 the user submitted. Used to pre-fill the WhatsApp form when
// they come back via "Use a different number" or after a failed attempt.
const lastWhatsapp = ref("");

const APP_DEEPLINK = import.meta.env.VITE_APP_DEEPLINK || "ai.beconfident.app://home";
const APP_DEEPLINK_COMMUNITY = "ai.beconfident.app://community";

const TERMINAL_JOIN_CODES: ReadonlySet<JoinErrorCode> = new Set([
  "not_confirmed",
  "event_full",
  "not_live_yet",
  "event_ended",
  "event_cancelled",
]);

function toErrorCode(error: unknown, fallback: GateErrorCode = "network"): GateErrorCode {
  if (error instanceof ApiError && error.code) return error.code;
  return fallback;
}

function toRetryAfter(error: unknown): number | undefined {
  if (error instanceof ApiError && error.retryAfterSeconds !== null) {
    return error.retryAfterSeconds;
  }
  return undefined;
}

function failFromError(error: unknown): void {
  const code = toErrorCode(error);
  const retry = toRetryAfter(error);
  const event = error instanceof ApiError ? (error.event ?? undefined) : undefined;
  machine.fail({
    code,
    ...(retry !== undefined ? { retryAfterSeconds: retry } : {}),
    ...(event ? { event } : {}),
  });
}

function isJoinErrorCode(code: GateErrorCode): code is JoinErrorCode {
  return TERMINAL_JOIN_CODES.has(code as JoinErrorCode);
}

async function onSubmitWhatsapp(whatsapp: string): Promise<void> {
  lastWhatsapp.value = whatsapp;
  machine.submitWhatsapp(whatsapp);
  track({ type: "otp_send_requested", eventId: props.eventId });
  try {
    await sendOtp(whatsapp);
    machine.otpSent();
    track({ type: "otp_sent", eventId: props.eventId });
  } catch (error) {
    const code = toErrorCode(error);
    track({ type: "otp_send_failed", eventId: props.eventId, reason: code });
    failFromError(error);
  }
}

async function onSubmitCode(code: string): Promise<void> {
  const current = machine.state.value;
  if (current.kind !== "awaiting_code") return;
  const { whatsapp, attemptsRemaining } = current;
  machine.submitCode();
  track({ type: "otp_submitted", eventId: props.eventId, attemptsRemaining });
  try {
    const token = await validateOtp(whatsapp, code);
    setToken(token);
    machine.codeValidated(props.eventId);
    track({ type: "otp_validated", eventId: props.eventId });
    await loadAfterAuth();
  } catch (error) {
    if (error instanceof ApiError && error.code === "otp_invalid") {
      machine.codeInvalid();
      otpFormRef.value?.clear();
      const next = machine.state.value;
      if (next.kind === "awaiting_code") {
        track({
          type: "otp_invalid",
          eventId: props.eventId,
          attemptsRemaining: next.attemptsRemaining,
        });
      } else {
        track({ type: "otp_locked", eventId: props.eventId });
      }
      return;
    }
    failFromError(error);
  }
}

/**
 * Run the post-authentication checks before fetching the event:
 *   1. GET /app/me — checks the user's subscription state.
 *   2. GET /events/:id — pulls the event detail used by the preview and
 *      paywall screens.
 *
 * Used after a fresh OTP validation and again on mount when a stored JWT
 * is rehydrated. A stale token (401) drops the user back to the WhatsApp
 * step silently. An unpaid account routes to the dedicated paywall screen
 * (with the event preview above it) so the user sees what they're missing.
 */
async function loadAfterAuth(): Promise<void> {
  let isPaid: boolean;
  try {
    const me = await getMe();
    // Everything tracked from here on carries the user id — including
    // meeting_entered, which is what proxies attendance.
    identifyUser(me.userId);
    isPaid = me.isPaid;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      clearToken();
      machine.reset();
      track({ type: "session_expired", eventId: props.eventId });
      resetUser();
      return;
    }
    failFromError(error);
    return;
  }

  if (isPaid) {
    await runFetchEvent();
    return;
  }

  // Unpaid: fetch the event so the paywall screen can show what the user
  // is missing. If the event itself is unreachable, fall back to the
  // generic subscription_required error screen.
  track({
    type: "join_failed",
    eventId: props.eventId,
    reason: "subscription_required",
  });
  try {
    const event = await getEventDetail(props.eventId);
    track({
      type: "event_fetched",
      eventId: props.eventId,
      status: event.status,
      isFull: event.isFull,
    });
    machine.paywall(event);
  } catch {
    machine.fail({ code: "subscription_required" });
  }
}

async function runFetchEvent(): Promise<void> {
  try {
    const event = await getEventDetail(props.eventId);
    track({
      type: "event_fetched",
      eventId: props.eventId,
      status: event.status,
      isFull: event.isFull,
    });
    machine.eventFetched(event);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Stored token is stale — silently drop it and restart the OTP flow.
      clearToken();
      machine.reset();
      track({ type: "session_expired", eventId: props.eventId });
      resetUser();
      return;
    }
    const code = toErrorCode(error);
    track({ type: "event_fetch_failed", eventId: props.eventId, reason: code });
    failFromError(error);
  }
}

async function onJoinClicked(): Promise<void> {
  const current = machine.state.value;
  if (current.kind !== "awaiting_join") return;

  machine.requestJoin();
  track({ type: "join_requested", eventId: props.eventId });

  try {
    const result = await joinEvent(props.eventId);
    track({
      type: "join_succeeded",
      eventId: props.eventId,
      attendanceKind: result.attendanceKind,
    });
    track({
      type: "meeting_entered",
      eventId: props.eventId,
      attendanceKind: result.attendanceKind,
    });
    // Navigate directly to the meeting. The preview's submitting spinner is
    // the last thing painted before the page unloads.
    window.location.href = result.accessLink;
    return;
  } catch (error) {
    const code = toErrorCode(error);

    if (code === "capacity_race") {
      track({ type: "join_capacity_refetch", eventId: props.eventId });
      machine.joinCapacityRace();
      await runFetchEvent();
      return;
    }

    track({ type: "join_failed", eventId: props.eventId, reason: code });

    if (code === "rate_limited") {
      failFromError(error);
      return;
    }

    if (isJoinErrorCode(code)) {
      const event = error instanceof ApiError ? (error.event ?? undefined) : undefined;
      machine.joinFailed(code, event);
      return;
    }

    const inlineError: JoinInlineError = code === "network" ? "network" : "unknown";
    machine.joinInlineFailed(inlineError);
  }
}

async function onResendOtp(): Promise<void> {
  const current = machine.state.value;
  const whatsapp =
    current.kind === "awaiting_code" || current.kind === "validating_otp" ? current.whatsapp : null;
  if (!whatsapp) return;

  resendingOtp.value = true;
  try {
    await sendOtp(whatsapp);
    track({ type: "otp_resent", eventId: props.eventId });
    if (machine.state.value.kind === "error") {
      machine.reset();
      machine.submitWhatsapp(whatsapp);
      machine.otpSent();
    }
  } catch (error) {
    const code = toErrorCode(error);
    track({ type: "otp_send_failed", eventId: props.eventId, reason: code });
    failFromError(error);
  } finally {
    resendingOtp.value = false;
  }
}

function onChangeNumber(): void {
  machine.changeNumber();
  track({ type: "whatsapp_changed", eventId: props.eventId });
}

function onTryAnotherNumber(): void {
  clearToken();
  machine.changeNumber();
  track({ type: "whatsapp_changed", eventId: props.eventId });
  // Explicit identity switch: drop the previous user before the next OTP.
  resetUser();
}

function onContactSupport(): void {
  track({ type: "support_clicked", eventId: props.eventId });
  window.location.href = t("support.url");
}

function onOpenAppFromPreview(): void {
  track({ type: "open_app_clicked", eventId: props.eventId, source: "preview" });
  // Fire the community deep link and race a 1.5s timer that falls back to
  // the right app store. If the OS hands the link to the installed app,
  // the page goes hidden before the timer fires and we cancel the fallback.
  const fallback = window.setTimeout(() => {
    window.location.href = resolveStoreUrl();
  }, 1500);
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) window.clearTimeout(fallback);
    },
    { once: true },
  );
  window.location.href = APP_DEEPLINK_COMMUNITY;
}

function onOpenAppFromError(): void {
  track({ type: "open_app_clicked", eventId: props.eventId, source: "error" });
  window.location.href = APP_DEEPLINK;
}

function onSubscribe(): void {
  track({ type: "open_app_clicked", eventId: props.eventId, source: "error" });
  // "Baixar o app e assinar" sends the user to the correct app store.
  // Desktop / unknown UAs fall back to the marketing site.
  window.location.href = resolveStoreUrl();
}

function onErrorRetry(): void {
  const current = machine.state.value;
  const code = current.kind === "error" ? current.error.code : "unknown";
  track({ type: "error_retried", eventId: props.eventId, errorCode: code });
  machine.reset();
}

function onCloseError(): void {
  track({ type: "error_closed", eventId: props.eventId });
  window.close();
}

onMounted(() => {
  applyFromEnvironment();
  track({ type: "page_loaded", eventId: props.eventId });
  if (hasValidToken()) {
    track({ type: "session_resumed", eventId: props.eventId });
    machine.resume(props.eventId);
    void loadAfterAuth();
  }
});
</script>

<template>
  <main class="gate-shell">
    <BrandHeader
      v-if="machine.state.value.kind !== 'awaiting_join' && machine.state.value.kind !== 'paywall'"
    />

    <template
      v-if="
        machine.state.value.kind === 'awaiting_whatsapp' ||
        machine.state.value.kind === 'sending_otp'
      "
    >
      <WhatsappForm
        :submitting="machine.state.value.kind === 'sending_otp'"
        :initial-e164="lastWhatsapp"
        @submit="onSubmitWhatsapp"
      />
    </template>

    <template
      v-else-if="
        machine.state.value.kind === 'awaiting_code' ||
        machine.state.value.kind === 'validating_otp'
      "
    >
      <OtpForm
        ref="otpFormRef"
        :validating="machine.state.value.kind === 'validating_otp'"
        :resending="resendingOtp"
        :whatsapp="machine.state.value.whatsapp"
        @submit="onSubmitCode"
        @resend="onResendOtp"
        @change-number="onChangeNumber"
      />
    </template>

    <template v-else-if="machine.state.value.kind === 'fetching_event'">
      <section class="gate-card gate-loading" aria-live="polite">
        <div class="gate-spinner gate-loading__spinner" aria-hidden="true" />
        <p class="gate-subheading">{{ $t("preview.fetching") }}</p>
      </section>
    </template>

    <template v-else-if="machine.state.value.kind === 'awaiting_join'">
      <EventPreviewScreen
        :event="machine.state.value.event"
        :submitting="machine.state.value.submitting"
        :inline-error="machine.state.value.inlineError"
        @join="onJoinClicked"
        @open-app="onOpenAppFromPreview"
      />
    </template>

    <template v-else-if="machine.state.value.kind === 'paywall'">
      <EventPreviewScreen
        :event="machine.state.value.event"
        :submitting="false"
        :inline-error="null"
        :paywalled="true"
      />
      <SubscriptionRequiredScreen
        @subscribe="onSubscribe"
        @try-another-number="onTryAnotherNumber"
        @contact-support="onContactSupport"
      />
    </template>

    <template v-else-if="machine.state.value.kind === 'error'">
      <ErrorScreen
        :error="machine.state.value.error"
        @open-app="onOpenAppFromError"
        @retry="onErrorRetry"
        @resend="onResendOtp"
        @close="onCloseError"
      />
    </template>
  </main>
</template>

<style scoped>
.gate-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-8);
}

.gate-loading__spinner {
  width: 28px;
  height: 28px;
  border-width: 3px;
  border-color: var(--color-primary-muted);
  border-top-color: var(--color-primary);
}
</style>
